import styled, { css } from 'styled-components';

const VARIANTS = {
  primary: {
    bg: 'linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%)',
    bgHover: 'linear-gradient(135deg, #1E3A5F 0%, #2E5B88 60%, #4FB3BF 100%)',
    color: '#ffffff',
    shadow: '46, 91, 136',
    ring: '79, 179, 191'
  },
  secondary: {
    bg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    bgHover: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    color: '#ffffff',
    shadow: '5, 150, 105',
    ring: '16, 185, 129'
  },
  danger: {
    bg: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    bgHover: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
    color: '#ffffff',
    shadow: '220, 38, 38',
    ring: '239, 68, 68'
  },
  success: {
    bg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    bgHover: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    color: '#ffffff',
    shadow: '5, 150, 105',
    ring: '16, 185, 129'
  },
  warning: {
    bg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    bgHover: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
    color: '#ffffff',
    shadow: '217, 119, 6',
    ring: '245, 158, 11'
  }
};

const getV = v => VARIANTS[v] || VARIANTS.primary;

const sizeStyles = {
  small: css`
    padding: 8px 14px;
    font-size: 12.5px;
    border-radius: 10px;
    min-width: ${props => props.fullWidth ? '100%' : '80px'};

    svg {
      width: 14px;
      height: 14px;
    }
  `,
  large: css`
    padding: 14px 24px;
    font-size: 15.5px;
    border-radius: 14px;
    min-width: ${props => props.fullWidth ? '100%' : '140px'};

    svg {
      width: 18px;
      height: 18px;
    }
  `,
  default: css`
    padding: 11px 20px;
    font-size: 14px;
    border-radius: 12px;
    min-width: ${props => props.fullWidth ? '100%' : '120px'};

    svg {
      width: 16px;
      height: 16px;
    }
  `
};

const ButtonBase = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 600;
  letter-spacing: 0.1px;
  cursor: pointer;
  overflow: hidden;
  isolation: isolate;
  transition:
    transform 160ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 180ms ease,
    filter 180ms ease,
    background 180ms ease,
    border-color 180ms ease,
    color 180ms ease;

  ${props => sizeStyles[props.size] || sizeStyles.default}

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    filter: grayscale(0.3);
  }

  svg {
    flex-shrink: 0;
    stroke-width: 2.2;
  }
`;

const filledVariant = css`
  background: ${props => getV(props.variant).bg};
  color: ${props => getV(props.variant).color};
  border: 1px solid transparent;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.3) inset,
    0 -1px 0 rgba(0, 0, 0, 0.1) inset,
    0 4px 10px -2px rgba(${props => getV(props.variant).shadow}, 0.4),
    0 2px 4px rgba(15, 23, 42, 0.06);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: ${props => getV(props.variant).bgHover};
    filter: brightness(1.03) saturate(1.05);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.35) inset,
      0 -1px 0 rgba(0, 0, 0, 0.1) inset,
      0 8px 18px -4px rgba(${props => getV(props.variant).shadow}, 0.5),
      0 2px 6px rgba(15, 23, 42, 0.08);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.97);
    transition-duration: 80ms;
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.3) inset,
      0 4px 10px -2px rgba(${props => getV(props.variant).shadow}, 0.4),
      0 0 0 3px rgba(${props => getV(props.variant).ring}, 0.28);
  }
`;

const outlineVariant = css`
  background: #ffffff;
  color: #2E5B88;
  border: 1px solid rgba(79, 179, 191, 0.4);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 1px 2px rgba(15, 23, 42, 0.05);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: linear-gradient(135deg, rgba(46, 91, 136, 0.05) 0%, rgba(79, 179, 191, 0.08) 100%);
    border-color: rgba(79, 179, 191, 0.6);
    color: #1E3A5F;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 4px 10px -3px rgba(79, 179, 191, 0.28),
      0 1px 3px rgba(15, 23, 42, 0.06);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.97);
    transition-duration: 80ms;
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 0 0 3px rgba(79, 179, 191, 0.24);
  }
`;

const ghostVariant = css`
  background: transparent;
  color: #2E5B88;
  border: 1px solid transparent;

  &:hover:not(:disabled) {
    background: rgba(79, 179, 191, 0.1);
    color: #1E3A5F;
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
    transition-duration: 80ms;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(79, 179, 191, 0.22);
  }
`;

export const Button = styled(ButtonBase)`
  ${props => {
    if (props.variant === 'outline') return outlineVariant;
    if (props.variant === 'ghost') return ghostVariant;
    return filledVariant;
  }}
`;

export const IconButton = styled(ButtonBase)`
  background: #ffffff;
  color: #2E5B88;
  padding: 9px;
  min-width: auto;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 1px 2px rgba(15, 23, 42, 0.05);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: rgba(79, 179, 191, 0.08);
    border-color: rgba(79, 179, 191, 0.4);
  }

  &:active:not(:disabled) {
    transform: scale(0.93);
    transition-duration: 80ms;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(79, 179, 191, 0.24);
  }
`;
